import { AppData } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Logger } from "./logger";
import MultiBannerAds, { BannerCreative } from "./multi-banner-ads";

export default class MultiBannerManager {
    adsByUrl: { [url: string]: MultiBannerAds } = {};
    creativesByUrl: { [url: string]: BannerCreative[] } = {};
    fetchPromiseByUrl: { [url: string]: Promise<BannerCreative[]> } = {};
    adImpressionFiredByUrl: { [url: string]: boolean } = {};
    rotationIndexByL1: { [l1Index: number]: number } = {};
    pendingBannerByL1: { [l1Index: number]: BannerCreative | null } = {};

    init = (): void => {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();
        if (!appApiConfig?.appConfig?.enableMultipleBanner) {
            Logger.printLog('info', 'MultiBannerManager.init() - multiple banner disabled', '');
            return;
        }
        const channels = appApiConfig.appConfig.channels || [];
        channels.forEach((channel: any) => {
            const adTagUrl = channel?.multiplebannerad_image;
            if (!adTagUrl || this.adsByUrl[adTagUrl]) {
                return;
            }
            this.adsByUrl[adTagUrl] = new MultiBannerAds(adTagUrl);
        });
    }


    _loadCreatives = (adTagUrl: string): Promise<BannerCreative[]> => {
        const ad = this.adsByUrl[adTagUrl];
        if (!ad) {
            return Promise.resolve([]);
        }
        if (!this.fetchPromiseByUrl[adTagUrl]) {
            this.fetchPromiseByUrl[adTagUrl] = ad.fetchAndResolve().then((creatives: BannerCreative[]) => {
                this.creativesByUrl[adTagUrl] = creatives;
                Logger.printLog('info', 'MultiBannerManager._loadCreatives() - creatives resolved: ' + creatives.length, adTagUrl);
                return creatives;
            });
        }
        return this.fetchPromiseByUrl[adTagUrl];
    }

    refreshCreatives = (l1Index: number): void => {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();
        const adTagUrl = appApiConfig?.appConfig?.channels?.[l1Index]?.multiplebannerad_image;
        if (!adTagUrl) {
            return;
        }
        this._loadCreatives(adTagUrl);
    }

    getBanner = (l1Index: number): BannerCreative | null => {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();
        if (!appApiConfig?.appConfig?.enableMultipleBanner) {
            return null;
        }

        const adTagUrl = appApiConfig.appConfig.channels?.[l1Index]?.multiplebannerad_image;
        if (!adTagUrl) {
            return null;
        }

        const creatives = this.creativesByUrl[adTagUrl];
        if (!creatives) {
            this._loadCreatives(adTagUrl);
            return null;
        }
        if (creatives.length === 0) {
            return null;
        }

        const pending = this.pendingBannerByL1[l1Index];
        if (pending) {
            Logger.printLog('info', 'MultiBannerManager.getBanner() - L1 ' + l1Index + ' reusing pending creative', pending.imageUrl);
            return pending;
        }

        const nextIndex = ((this.rotationIndexByL1[l1Index] ?? -1) + 1) % creatives.length;
        this.rotationIndexByL1[l1Index] = nextIndex;
        const creative = creatives[nextIndex];
        if (!creative) {
            return null;
        }
        this.pendingBannerByL1[l1Index] = creative;
        const ad = this.adsByUrl[adTagUrl];
        if (ad) {
            if (!this.adImpressionFiredByUrl[adTagUrl]) {
                this.adImpressionFiredByUrl[adTagUrl] = true;
                ad.fireAdImpression();
            }
            ad.fireImpressions(creative);
            ad.fireTrackingEvent(creative);
            ad.prefetchNextBanner(nextIndex);
        }

        Logger.printLog('info', 'MultiBannerManager.getBanner() - L1 ' + l1Index + ' showing creative ' + nextIndex, creative.imageUrl);
        return creative;
    }

    releaseBanner = (l1Index: number): void => {
        this.pendingBannerByL1[l1Index] = null;
    }
}
